import { Box, Card, Typography } from '@mui/material';
import {
  CarouselItem,
  FilePreviewList,
  UploadImagesComponent,
} from 'src/hooks/useForm';
import Images from '../ImageCarousel';
import { useForm } from './Context';
import * as backend from 'src/backend';
import { BUCKET_NAME, S3_BASE_URL } from 'src/config';
import { CarouselControlsArrowsIndex } from '../carousel';
import { useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

export async function uploadImages({ images, folder, source = null }) {
  return await Promise.all(
    images.map(async (image) => {
      if (typeof image === 'string') return image;

      const { url, file, data } = image;

      if (url) return url;

      return await backend.image.upload(
        file,
        data,
        {
          folder,
          filetype: file.type,
        },
        folder,
        source
      );
    })
  );
}

export async function uploadImagesAndReturnURLWithName({
  images,
  folder,
  source = null,
}) {
  let result = [];
  for (let img of images) {
    if (typeof img === 'string') {
      result.push({ url: img });
      continue;
    }

    const { url, file, data } = img;
    if (url) {
      result.push(img);
    } else {
      const uploadedUrl = await uploadImages({
        images: [{ file, data }],
        folder,
        source,
      });
      result.push({ url: uploadedUrl[0], name: file?.name });
    }
  }
  return result;
}

export default function UploadImages({
  id,
  label,
  defaultValue,
  maxFiles,
  handleFileCountExceed,
  documentLayout,
  fileListText,
  includeLink,
  ...props
}) {
  const { readonly, error, value, handleChange } = useForm(id, defaultValue);

  if (readonly) {
    if (!Array.isArray(value) || value.length === 0) {
      return null;
    }
    const files = value.map((url) => `${S3_BASE_URL}/${BUCKET_NAME}/${url}`);
    if (documentLayout) {
      return (
        <FilePreviewList
          text={fileListText}
          files={files}
          includeLink={includeLink}
        />
      );
    }
    return (
      <Images
        width="100%"
        height="300px"
        images={files}
        objectFit="contain"
        downloadName={label}
      />
    );
  }

  return (
    <>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {label}
      </Typography>

      <UploadImagesComponent
        label={label}
        value={value ?? []}
        onChange={(newValue) => handleChange({ target: { value: newValue } })}
        error={error}
        objectFit="contain"
        maxFiles={maxFiles}
        handleFileCountExceed={handleFileCountExceed}
        documentLayout={documentLayout}
        fileListText={fileListText}
        {...props}
      />
    </>
  );
}

export function FilePreviewCarousel({ id, width = 420, height = 210 }) {
  const { value: files } = useForm(id);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const hasFiles = Array.isArray(files) && files.length > 0;

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const handleSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    handleSelect();
    emblaApi.on('select', handleSelect);
    emblaApi.on('reInit', handleSelect);

    return () => {
      emblaApi.off('select', handleSelect);
      emblaApi.off('reInit', handleSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !hasFiles) {
      return;
    }

    const intervalId = window.setInterval(() => {
      emblaApi.scrollNext();
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [emblaApi, hasFiles]);

  if (!hasFiles) {
    return null;
  }

  return (
    <Card
      sx={{
        width,
        height,
        mt: 1,
        borderRadius: 1,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        ref={emblaRef}
        sx={{ overflow: 'hidden', width: '100%', height: '100%' }}
      >
        <Box sx={{ display: 'flex', height: '100%' }}>
          {files.map((item) => (
            <Box
              key={item.title || item.file}
              sx={{
                flex: '0 0 100%',
                minWidth: 0,
                height: '100%',
              }}
            >
              <CarouselItem
                item={item}
                width={width}
                height={height}
                objectFit="cover"
              />
            </Box>
          ))}
        </Box>
      </Box>

      <CarouselControlsArrowsIndex
        index={currentIndex}
        total={files.length}
        onNext={() => emblaApi?.scrollNext()}
        onPrevious={() => emblaApi?.scrollPrev()}
      />
    </Card>
  );
}
